export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export function formatPrice(price?: number | null, currency = 'EUR'): string {
  if (!price) return '—'
  return new Intl.NumberFormat('sk-SK', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price)
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('sk-SK', { dateStyle: 'medium' }).format(new Date(date))
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('sk-SK', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(date))
}

/** Čas „09:05“. */
export function formatTime(date: string | Date): string {
  return new Intl.DateTimeFormat('sk-SK', { hour: '2-digit', minute: '2-digit' }).format(new Date(date))
}

/** „práve teraz“, „pred 5 min“, „pred 3 h“, „pred 2 d“, od týždňa dátum; budúci čas je „práve teraz“, chýbajúci alebo neplatný null. */
export function formatRelative(date: string | Date | null | undefined, now: Date = new Date()): string | null {
  if (!date) return null
  const time = new Date(date).getTime()
  if (!Number.isFinite(time)) return null
  const minutes = Math.floor((now.getTime() - time) / 60_000)
  if (minutes < 1) return 'práve teraz'
  if (minutes < 60) return `pred ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `pred ${hours} h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `pred ${days} d`
  return formatDate(date)
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

/** Veľkosť súboru v násobkoch 1024: „512 B“, „1,5 KB“, „12 MB“, „19 GB“; chýbajúca hodnota „—“. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—'
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toLocaleString('sk-SK', { maximumFractionDigits: value < 10 ? 1 : 0 })} ${BYTE_UNITS[unit]}`
}

/** ISO/DB čas → hodnota pre <input type="datetime-local"> v lokálnom čase prehliadača. */
export function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Hodnota z <input type="datetime-local"> → ISO UTC. Surový string bez zóny by server
 *  parsoval vo VLASTNEJ zóne (kontajner beží v UTC) a uložený čas by sa posunul. */
export function datetimeLocalToIso(value: string): string {
  return new Date(value).toISOString()
}

export function truncate(str: string, length: number): string {
  return str.length > length ? `${str.slice(0, length)}…` : str
}

/**
 * Sanitizes a numeric range-filter value (price/value "from"/"to") before it is
 * sent as a query param. Returns the original string only if it parses to a
 * finite, non-negative number; otherwise undefined (param is dropped).
 *
 * The API DTOs validate these with `@Min(0)`, so a negative or junk value would
 * otherwise produce a 400. Sanitizing at the param layer is the single guard
 * that keeps every numeric filter (deals, properties, leads) from 400-ing,
 * regardless of what the input lets the user type.
 */
export function nonNegativeParam(value?: string | null): string | undefined {
  if (value == null || value === '') return undefined
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? value : undefined
}

import { PROPERTY_TYPE_LABELS, PROPERTY_TRANSACTION_TYPE_LABELS } from '@repo/types'

export function propertyTypeLabel(type: string): string {
  return PROPERTY_TYPE_LABELS[type] ?? type
}

export function transactionTypeLabel(type: string): string {
  return PROPERTY_TRANSACTION_TYPE_LABELS[type] ?? type
}

/** Pásmo prehliadača — v ňom admin zobrazuje časy, takže ho treba používať aj tam, kde sa čas alebo deň počíta mimo prehliadača. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Parametre, ktoré filtrujú podľa celého dňa „RRRR-MM-DD"; `adv` môže mať podmienku na dátum.
const DAY_FILTER_PARAMS = ['dateFrom', 'dateTo', 'dueDateFrom', 'dueDateTo', 'createdFrom', 'createdTo', 'adv']

/**
 * Pridá `timeZone` = pásmo prehliadača, keď požiadavka filtruje podľa dňa — API v ňom počíta začiatok a koniec
 * dňa, takže filter sedí s časmi v admine. Bez filtra dňa sa nepridá: staršie API by neznámy parameter odmietlo.
 */
export function withDayTimeZone(params: URLSearchParams): URLSearchParams {
  if (DAY_FILTER_PARAMS.some((key) => params.has(key))) params.set('timeZone', browserTimezone())
  return params
}
